import * as _ from 'lodash'
import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import * as systemActions from '../actions';
import * as employeeActions from '../employeeActions';
import * as fileActions from '../fileActions';
import localImage from '../localImage';
import componentServices, { IComponentServices } from '../componentServices';
import valueFilters from '../valueFilters';
import globalRelay from '../globalRelay';
import util from '../util';
import enums from '../enum';
import validator from '../validator';
import ChangeHistoryModal from './jobCostChangeHistoryModal';

// TODO: move this out of here - maybe to "sharedHigherOrderComponents"
const sortableComponent = InnerComponent => {
	return class Sortable extends React.Component<any, any> {
		constructor (props) {
			super(props);

			this.state = {
				sortKey: props.initialSortKey,
				sortDirection: typeof props.initialSortDirection === 'undefined' ? 1 : props.initialSortDirection
			};

			this.updateSort = this.updateSort.bind(this);
		}
		updateSort (key) {
			if (key !== this.state.sortKey) {
				this.setState({
					sortKey: key,
					// User is changing sorted column, assume they want ascending at first
					sortDirection: 1
				});
			}
			else {
				this.setState({
					sortDirection: this.state.sortDirection * -1
				});
			}
		}
		_sortList (list) {
			const key = this.state.sortKey;
			const direction = this.state.sortDirection;

			if (!key) {
				return list;
			}

			if (this.props.sortList) { return this.props.sortList(list, key, direction); }

			return list.sort((a, b) => {
				const a1 = (direction > 0) ? a : b;
				const b1 = (direction > 0) ? b : a;
				const aVal = (a1 && typeof a1.getAt === 'function') ? a1.getAt(key) : _.get(a1, key);
				const bVal = (b1 && typeof b1.getAt === 'function') ? b1.getAt(key) : _.get(b1, key);
				const aUndefined = typeof aVal === 'undefined';
				const bUndefined = typeof bVal === 'undefined';

				if (aUndefined && bUndefined) {
					return 0;
				}
				else if (aUndefined && !bUndefined) {
					return 1;
				}
				else if (!aUndefined && bUndefined) {
					return -1;
				}

				if (aVal < bVal) { return -1; }
				if (aVal > bVal) { return 1; }
				return 0;
			});
		}
		render () {
			const { list, ...childProps } = this.props;
			const sortedList = this._sortList(list);

			_.assign(childProps, { updateSort: this.updateSort, sortKey: this.state.sortKey, list: sortedList });

			return <InnerComponent {...childProps} />;
		}
	};
};

const Alert = ({ clear, isCloseable, message, type }) => {
	const button = (isCloseable) ? <button className="dismiss" onClick={ clear } /> : undefined;
	const className = 'alert ' + type;
	const messageLines = message.split('\n');
	const lines:JSX.Element[] = [];
	let i = 0;
	const length = messageLines.length;

	for (i = 0; i < length; i++) {
		let line = messageLines[i];

		if (line[line.length - 1] === '.') {
			line = line.substr(0, line.length - 1);
		}

		line = line.replace(/\s\.\s/, ' &bull; ');

		if (i < length - 1) {
			line += ' &bull; ';
		}

		lines.push(<span key={ i } dangerouslySetInnerHTML={ { __html: line } } />);
	}

	return (<section className={className}>{button}{lines}</section>);
};

export const AuthorizedContent = ({ isAuthorized, children }: { isAuthorized: (services: IComponentServices) => boolean, children: any } ) => {
	const authResult = isAuthorized(componentServices);

	return authResult? children: null;
}

class Avatar extends React.PureComponent<any> {
	constructor (props) {
		super(props);

		this.handleFileChange = this.handleFileChange.bind(this);
	}

	handleFileChange (e) {
		e.preventDefault();
		const file = e.target.files[0];

		localImage.create(file).fitToBounds(this.props.imageWidth, this.props.imageHeight, !!this.props.letterbox).then(result => {
			this.props.onImage(result);
		});
	}

	render () {
		const style: React.CSSProperties = {};
		const { imageUrl, canEdit, className, label } = this.props;

		if (imageUrl !== undefined) {
			style.backgroundImage = 'url(' + imageUrl + ')';
		}
		const buttonClass = !imageUrl && !!canEdit ? 'userPhoto add' : 'userPhoto';
		const input = (canEdit) ? <input type="file" onChange={this.handleFileChange} accept="image/*" capture="user" /> : undefined;

		return (
			<div className={className}>
				<div className={buttonClass} style={style} />
				{input}
				{label}
			</div>
		);
	}
}

class AsyncButton extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.state = { processing: false };
		this.handleClick = this.handleClick.bind(this);
	}

	handleClick () {
		if (!this.state.processing) {
			this.setState({ processing: true });

			this.props.asyncAction()
				.finally(() => {
					this.setState({ processing: false });
				});
		}
	}

	render () {
		return <Button className={ this.props.className } label={ this.props.label } onClick={ this.handleClick } processing={ this.state.processing } />;
	}
}

const Button = ({ className, label, onClick, processing = false }) => {
	const spinner = processing ? (<Spinner />) : null;
	const classes = processing ? [className, 'loading'] : [className];

	return (<button className={classes.join(' ')} onClick={ onClick }>{ spinner }<span>{label}</span></button>);
};

const ColumnSelectorColumn = ({ children }) => {
	return (
		<div className="column">
			<div className="scrollable">
				{children}
			</div>
		</div>
	);
};

class ColumnSelectorItem extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.onClicked = this.onClicked.bind(this);
	}

	onClicked (e) {
		e.preventDefault();
		const fullPath = (this.props.keyPath) ? this.props.keyPath + this.props.pathSeparator + this.props.nodeKey : this.props.nodeKey;

		this.props.handleClicked({ selectedPath: fullPath });
	}

	render () {
		const { title, classNames } = this.props;
		const className = ((!!classNames && classNames.length) ? classNames.join(' ') : '');

		return (<button className={className} onClick={this.onClicked}><span>{title}</span></button>);
	}
}

// helper function for ColumnSelector
const getSelectedNodes = function (nodes, selectedPath, pathSeparator, keyPath = undefined, selected:any[] = []) {
	_.forEach(nodes, (node) => {
		const nodePath = (keyPath) ? keyPath + pathSeparator + node.key : node.key;
		const isSelected = (selectedPath && selectedPath.indexOf(nodePath) === 0);

		if (isSelected) {
			selected.push(node);
		}

		if (isSelected && node.children) {
			getSelectedNodes(node.children, selectedPath, pathSeparator, nodePath, selected);
		}
	});

	return selected;
};

class ColumnSelector extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.state = {
			pathSeparator: props.pathSeparator || '.'
		};

		this.next = this.next.bind(this);
		this.handleNodeClicked = this.handleNodeClicked.bind(this);
	}

	next (e) {
		e.preventDefault();

		const selectedNodes = getSelectedNodes(this.props.nodes, this.props.selectedPath, this.state.pathSeparator);

		this.props.stateChanged({ type: 'completed', selectedPath: this.props.selectedPath, nodes: selectedNodes, pathSeparator: this.state.pathSeparator });
	}

	handleNodeClicked (data) {
		const selectedNodes = getSelectedNodes(this.props.nodes, data.selectedPath, this.state.pathSeparator);

		this.props.stateChanged({ type: 'selected', selectedPath: data.selectedPath, nodes: selectedNodes, pathSeparator: this.state.pathSeparator });
	}

	render () {
		const { classNames, selectedPath, nextDisabled } = this.props;
		const pathSeparator = this.state.pathSeparator;
		const clickHandler = this.handleNodeClicked;

		// converts non-root nodes into ColumnSelectorColumns with ColumnSelectorItems
		const transform = function (nodes, keyPath = undefined, columns:JSX.Element[] = []) {
			const childItems: JSX.Element[] = [];

			_.forEach(nodes, (node) => {
				const nodePath = (keyPath) ? keyPath + pathSeparator + node.key : node.key;
				const selected = (selectedPath && selectedPath.indexOf(nodePath) === 0);
				const nodeClassNames = node.classes || [];

				if (selected) {
					nodeClassNames.push('on');
				}

				const childItem = (
					<ColumnSelectorItem
						classNames={nodeClassNames}
						key={nodePath}
						keyPath={keyPath}
						pathSeparator={pathSeparator}
						nodeKey={node.key}
						title={node.title}
						handleClicked={ clickHandler } />
				);

				childItems.push(childItem);

				if (selected && node.children) {
					transform(node.children, nodePath, columns);
				}
			});

			if (childItems.length > 0) {
				const columnKey = 'column_' + columns.length;

				// insert this column at the front because its descendents have already inserted theres
				columns.unshift(<ColumnSelectorColumn key={ columnKey }>{ childItems }</ColumnSelectorColumn>);
			}

			return columns;
		};

		const children = transform(this.props.nodes);

		return (
			<section className={['columnSelector'].concat(classNames).join(' ')}>
				<header>
					<span className="centered">Select</span>
					<button className={ 'icon iconOnly next' + (nextDisabled ? ' disabled' : '') } onClick={this.next}><span /></button>
				</header>
				<div className="columns clearfix">

					{ children }

				</div>
			</section>
		);
	}
}

const Confirm = ({ cancelLabel = 'Cancel', confirmLabel = 'OK', cancelHandler, confirmHandler, title, message, classes = undefined }) => {
	const buttons:JSX.Element[] = [];
	const classNames = ['alert'].concat(classes || ['urgent']); // NOTE: urgent and error are supported

	buttons.push(<button key="cancel" onClick={ cancelHandler }><span>{ cancelLabel }</span></button>);
	buttons.push(<button key="confirm" className="delete" onClick={ confirmHandler }><span>{ confirmLabel }</span></button>);

	return (<div className={ classNames.join(' ') }>
		<section>
			<header><span>{ title }</span></header>
			<div className="content">
				{ message }
				<div className="actions">
					{ buttons }
				</div>
			</div>
		</section>
	</div>);
};

const ContextMenu = ({ items, style, disableIcons = false }: { items: any[], style?: React.CSSProperties, disableIcons?: boolean }) => {
	const baseClassNames = disableIcons ? ['button'] : ['button', 'icon', 'iconLabel'];
	const rows = _.map(items, item => {
		const clickHandler = item.action ? (e) => { if (!item.href) { e.preventDefault(); } item.action(); } : undefined;
		const classNames = _.concat(baseClassNames, (item.className || '').split(' '));

		return <li key={item.label}><a className={ classNames.join(' ') } href={ item.href || '#'} onClick={ clickHandler } target={ item.target }><span>{ item.label }</span></a></li>;
	});

	return	(<div className="content scrollable" style={ style }>
		<ul className="menu">
			{ rows }
		</ul>
	</div>);
};

class ContractorLockSection extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.state = this.getStateFromProps(props);
	}

	UNSAFE_componentWillReceiveProps (nextProps) {
		this.setState(this.getStateFromProps(nextProps));
	}

	getStateFromProps (nextProps) {
		const nextState = _.assign({}, this.state, { statusEmployee: undefined });
		const { contractor } = nextProps;

		if (contractor && contractor.currentStatus === 'locked' && contractor.currentStatusItem.createdById) {
			nextState.statusEmployee = employeeActions.getEmployeeByUserKey(contractor.currentStatusItem.createdById);

			if (!nextState.statusEmployee) {
				employeeActions.fetchEmployee({ userKey: contractor.currentStatusItem.createdById });
			}
		}

		return nextState;
	}
	render () {
		const { contractor } = this.props;
		const { statusEmployee } = this.state;

		return (contractor && statusEmployee ? <section className="alert error"><span>Contractor locked by { statusEmployee.fullName } { util.dateHelper.formatDate(contractor.currentStatusItem.dateCreated, { userFriendly: true }) }</span> • <span>{ contractor.currentStatusItem.description }</span></section> : null);
	}
}

class CurrencyInput extends React.Component<any, any> {
	private _innerValue;

	constructor (props) {
		super(props);

		this._innerValue = '';
		this.state = { hasFocus: false };

		this.handleOnBlur = this.handleOnBlur.bind(this);
		this.handleOnFocus = this.handleOnFocus.bind(this);
		this.handleOnChange = this.handleOnChange.bind(this);
	}

	handleOnBlur (e) {
		this.setState({ hasFocus: false });

		// On blur, make sure we reset the value to a maximum of 2 digits.
		const currentValue = valueFilters.toNumber(e.target.value);

		e.target.value = isNaN(currentValue) ? e.target.value : currentValue.toFixed(2);

		// Call the parent onBlur if it is set
		if (this.props.onBlur) {
			this.props.onBlur(e);
		}
	}

	handleOnFocus (e) {
		const target = e.target;
		const numberVal = valueFilters.toNumber(valueFilters.cleanNumber(target.value));

		this._innerValue = isNaN(numberVal) ? '0' : numberVal.toString();

		this.setState({ hasFocus: true });

		// Call the parent onFocus if it is set
		if (this.props.onFocus) {
			this.props.onFocus(e);
		}

		setTimeout(() => { target.select(); }, 0);
	}

	handleOnChange (e) {
		const target = e.target;

		this._innerValue = valueFilters.cleanNumber(target.value) || '';
		target.value = this._innerValue;

		if (this.props.onChange) {
			this.props.onChange(e);
		}

		this.forceUpdate();
	}

	formatValue (val) {
		// If the user is currently typing in the field, do not format until the onBlur event
		if (this.state.hasFocus) {
			return this._innerValue;
		}
		else {
			return util.formatAsCurrency(val);
		}
	}

	render () {
		const { value, ...rest } = this.props;
		const childProps = _.omit(rest, 'onBlur', 'onChange', 'onFocus');

		return (
			<input type="text" onBlur={this.handleOnBlur} onFocus={this.handleOnFocus} onChange={ this.handleOnChange } value={this.formatValue(value)} {...childProps} />
		);
	}
}

class EditableDiv extends React.Component<any, any> {
	private _markup;
	private _el;
	private _mutationObserver;

	constructor (props) {
		super(props);

		this._markup = ('content' in props) ? props.content : '';
		this.handleBlur = this.handleBlur.bind(this);
		this.onContentChange = this.onContentChange.bind(this);
	}

	componentDidMount () {
		this._el.innerText = this._markup;
		this._mutationObserver = new MutationObserver(this.onContentChange);
		this._mutationObserver.observe(this._el, {
			childList: true, // To check for new lines
			subtree: true, // To check for nested elements
			characterData: true // To check for text modifications
		});
	}

	onContentChange () {
		const { onChange } = this.props;
		const { innerText } = this._el;

		if (innerText !== this._markup) {
			this._markup = innerText;

			if (onChange) {
				onChange({ target: { value: this._markup }});
			}
		}
	}

	handleBlur (e) {
		const { onBlur } = this.props;

		if (onBlur) {
			e.target = { value: e.target.innerText };

			onBlur(e);
		}
	}

	render () {
		const childProps = _.omit(this.props, 'onBlur', 'onChange', 'onInput', 'contentEditable');

		return <div contentEditable={ true } onBlur={ this.handleBlur } { ...childProps } ref={ el => { this._el = el; } } />;
	}
}

export function Expander(
	{ title, isExpanded = true, subTitle, tooltipText, children, actions, dragSourceRef, dragPreviewRef, dropTargetRef, connectDragPreview, connectDropTarget, connectDragSource, options, toggleExpander, classNames = [], contentClassNames = [], titleClassNames = [], subtitleClassNames = [], ...childProps }:
	{ title: string, isExpanded?: boolean, subTitle?: string, tooltipText?: string, children?: any, actions?: any, dragSourceRef?: any, dragPreviewRef?: any, dropTargetRef?: any, connectDragPreview?:any , connectDropTarget?: any, connectDragSource?: any, options?: any, toggleExpander?: any, classNames?: string[], contentClassNames?: string[], titleClassNames?: string[], subtitleClassNames?: string[] }) {
	const [ expanded, setExpanded ] = useState(isExpanded);
	const defaultClassNames = (toggleExpander? isExpanded: expanded) ? ['expander', 'expanded'] : ['expander', 'collapsed'];
	// const wrapper = connectDropTarget ? _.flow(connectDragPreview, connectDropTarget) : c => c;
	const allClassNames = _.concat(defaultClassNames, classNames);

	const childrenWithProps = React.Children.map(children, (child) => {
		return React.cloneElement(child, _.defaults({}, childProps));
	});

	const subTitleSpan = subTitle ? (<span className={ ['subtitle'].concat(...subtitleClassNames).join(' ') }>{subTitle}</span>) : null;
	const tooltip = tooltipText ? <TooltipTarget text={tooltipText}><button tabIndex={ -1 } className="icon iconOnly info"><span /></button></TooltipTarget> : null;
	const sectionRef = el => {
		if(dropTargetRef) {
			dropTargetRef(el);
		}
		if(dragPreviewRef) {
			dragPreviewRef(el);
		}
	}
	return <section ref={ sectionRef } className={allClassNames.join(' ')}>
		<header>
			{ dragSourceRef ? <button ref={ dragSourceRef } className="icon iconOnly drag" draggable="true"><span /></button> : null }
			<button onClick={() => toggleExpander? toggleExpander(): setExpanded(!expanded) } tabIndex={ -1 }><span className={ titleClassNames.join(' ') }>{title} {subTitleSpan} {tooltip} <span className="options">{ options }</span></span></button>
			<div className="icon iconExpander"><span /><span /></div>
			<div className="actions">{actions}</div>
		</header>
		<div className={contentClassNames.join(' ')}>
			{childrenWithProps}
		</div>
	</section>;


}
// class Expander extends React.Component<any, any> {
// 	constructor (props) {
// 		super(props);

// 		this.state = this.getStateFromProps(props);

// 		this.toggleExpander = this.toggleExpander.bind(this);
// 	}

// 	getStateFromProps (props) {
// 		return {
// 			expanded: ('initialExpanded' in props) ? !!props.initialExpanded : (this.state && 'expanded' in this.state) ? this.state.expanded : true
// 		};
// 	}

// 	toggleExpander (e) {
// 		const { toggleExpander } = this.props;
// 		e.preventDefault();
// 		if (toggleExpander) {
// 			toggleExpander();
// 		}
// 		else {
// 			const expanded = !this.state.expanded;
// 			this.setState({ expanded });
// 		}
// 	}

// 	render () {
// 		const { title, expanded, subTitle, tooltipText, children, actions, connectDragPreview, connectDropTarget, connectDragSource, options, toggleExpander, classNames = [], contentClassNames = [], ...childProps } = this.props;
// 		const defaultClassNames = (!toggleExpander && this.state.expanded || !!toggleExpander && expanded) ? ['expander', 'expanded'] : ['expander', 'collapsed'];
// 		const wrapper = connectDropTarget ? _.flow(connectDragPreview, connectDropTarget) : c => c;

// 		const allClassNames = _.concat(defaultClassNames, classNames);

// 		const childrenWithProps = React.Children.map(children, (child) => {
// 			return React.cloneElement(child, _.defaults({}, childProps));
// 		});

// 		const subTitleSpan = subTitle ? (<span className="subtitle">{subTitle}</span>) : null;
// 		const tooltip = tooltipText ? <TooltipTarget text={tooltipText}><button tabIndex={ -1 } className="icon iconOnly info"><span /></button></TooltipTarget> : null;

// 		return wrapper(
// 			<section className={allClassNames.join(' ')}>
// 				<header>
// 					{ connectDragSource ? connectDragSource(<button className="icon iconOnly drag" draggable="true"><span /></button>) : null }
// 					<button onClick={this.toggleExpander} tabIndex={ -1 }><span>{title} {subTitleSpan} {tooltip} <span className="options">{ options }</span></span></button>
// 					<div className="icon iconExpander"><span /><span /></div>
// 					<div className="actions">{actions}</div>
// 				</header>
// 				<div className={contentClassNames.join(' ')}>
// 					{childrenWithProps}
// 				</div>
// 			</section>
// 		);
// 	}
// }

const FileList = sortableComponent(class extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.handleFileChange = this.handleFileChange.bind(this);
	}

	handleFileChange (e) {
		const { relatedType, relatedTypeKey } = this.props;

		e.preventDefault();

		this.props.uploadFiles(e.target.files, relatedType, relatedTypeKey);
	}

	fileContextMenuHandler (file, e) {
		const opener = () => { systemActions.popModal(); this.fileOpenHandler(file); };
		const deleter = () => { systemActions.popModal(); this.props.deleteFile(file); };
		const items = [{ label: 'Open', className: (file.group === 'documents' ? 'pdf' : 'download'), action: opener }];

		if (file.source !== 'scan' || componentServices.securityContext.getCurrentUser().hasModule('scan-management')) {
			items.push({ label: 'Delete', className: 'removeItem delete', action: deleter });
		}

		const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

		systemActions.pushModal({ component: popup });
	}

	fileOpenHandler (file) {
		if (typeof (window) !== 'undefined') {
			window.open(file.url);
		}
	}

	handleSortClick (k) {
		this.props.updateSort(k);
	}

	getClassName (key, baseName?: string) {
		return (key === this.props.sortKey) ? _.concat(['sorted'], baseName).join(' ') : baseName;
	}

	render () {
		const { hideSource, title, subTitle, list, emptyTitle} = this.props;
		const rows = _.map(list, file => {
			const openHandler = this.fileOpenHandler.bind(this, file);
			const openClasses = ['icon', 'iconOnly'];

			if (file.group === 'documents') {
				openClasses.push('pdf');
			}
			else {
				openClasses.push('download');
			}

			return (<tr key={ file.key }>
				<td />
				<td><a onClick={ openHandler }>{ file.name }</a></td>
				{ hideSource ? null : <td>{ _.startCase(file.source) }</td> }
				<td>{ util.dateHelper.formatDate(file.dateCreated, { userFriendly: true }) }</td>
				<td className="action"><button className={ openClasses.join(' ') } onClick={ openHandler }><span /></button></td>
				<td className="action"><button className="icon iconOnly contextMenu" onClick={ this.fileContextMenuHandler.bind(this, file) }><span /></button></td>
				<td />
			</tr>);
		});

		const subTitleSpan = subTitle ? (<span className="subtitle">{subTitle}</span>) : null;
		const header = title ? (<header>
			<span>{ title } {subTitleSpan}</span>
			<div className="actions">
				<div className="button iconLabel upload">
					<span>Upload</span>
					<input type="file" onChange={this.handleFileChange} multiple={ true } />
				</div>
			</div>
		</header>) : null;

		const table = (<div className="inner">
			<table className="compact sortable" cellPadding="0" cellSpacing="0">
				<thead>
					<tr>
						<td />
						<td className={this.getClassName('name')} onClick={this.handleSortClick.bind(this, 'name')}>File Name</td>
						{ hideSource ? null : <td className={this.getClassName('source', 'name')} onClick={this.handleSortClick.bind(this, 'source')}>Source</td> }
						<td className={this.getClassName('dateCreated', 'date')} onClick={this.handleSortClick.bind(this, 'dateCreated')}>Date</td>
						<td className="action" />
						<td className="action" />
						<td />
					</tr>
				</thead>
				<tbody>
					{ rows }
				</tbody>
			</table>
		</div>)

		return (<section>
			{ header }
			<AsyncContent emptyContent={emptyTitle} hasData={ list.length }>{ table }</AsyncContent>
		</section>);
	}
});

class FractionInput extends React.Component<any, any> {
	private _innerValue;

	constructor (props) {
		super(props);

		this._innerValue = '';
		this.state = { hasFocus: false };

		this.handleEvent = this.handleEvent.bind(this);
	}

	handleEvent (e) {
		const target = e.target;

		this._innerValue = target.value;

		e.target.value = util.convertToDecimal(this._innerValue);

		if (e.type === 'focus') {
			this.setState({ hasFocus: true });
			setTimeout(() => { target.select(); }, 0);
		}
		else if (e.type === 'blur') {
			this.setState({ hasFocus: false });
			this._innerValue = '';
		}

		const handler = this.props['on' + _.upperFirst(e.type)];

		if (handler) {
			handler(e);
		}
	}

	formatValue (val) {
		if (this.state.hasFocus) {
			return this._innerValue;
		}
		else {
			return util.formatAsFraction(val);
		}
	}

	render () {
		const { value, ...rest } = this.props;
		const childProps = _.omit(rest, 'onBlur', 'onChange');

		return (
			<input type="text" onChange={ this.handleEvent } onFocus={ this.handleEvent } onBlur={ this.handleEvent } value={this.formatValue(value)} {...childProps}/>
		);
	}
}

const Header = ({ title, actions = undefined, options = undefined }: { title: string, actions?: JSX.Element[], options?: JSX.Element[]}) => {
	let keyCounter = 0;
	if (actions) {
		actions = _.map(actions, action => {
			if (!action.key) {
				return React.cloneElement(action, _.assign({ key: 'key-' + (++keyCounter) }, action.props));
			}
			return action;
		});
	}
	if (options) {
		options = _.map(options, option => {
			if (!option.key) {
				return React.cloneElement(option, _.assign({ key: 'key-' + (++keyCounter) }, option.props));
			}
			return option;
		});
	}
	return 	(<header>
		<span>{title} <span className="options">{ options }</span></span>
		<div className="actions">{ actions }</div>
	</header>);
};

// TODO: move this out of here
const jobCostRowOwner = (config, InnerComponent) => {
	return class JobCostRowOwner extends React.Component<any, any> {
		constructor (props) {
			super(props);

			this.getNextSortIndex = this.getNextSortIndex.bind(this);
			this.getRowClasses = this.getRowClasses.bind(this);
			this.handleItemDrop = this.handleItemDrop.bind(this);
			this.rowContextMenuHandler = this.rowContextMenuHandler.bind(this);
			this.sortRows = this.sortRows.bind(this);
		}
		rowContextMenuHandler (item, isValid, e) {
			const { jobCost } = this.props;
			const changeOrderKey = jobCost.currentChangeOrder && jobCost.currentChangeOrder.key;
			const lockTarget = jobCost.currentChangeOrder || jobCost;
			const items:any[] = [];
			const isLocked = lockTarget.currentStatus === 'locked' || lockTarget.isContract;

			if (isLocked || this.getRowClasses({ baseClassNames: undefined, item: item }).indexOf('readOnly') !== -1) {
				// if (item.isChange) {
				items.splice(0, 0, { label: 'History', className: 'changeRevert', action: this.showHistory.bind(this, item) });
				// }
			}
			else if (item.isSystemManaged) {
				items.splice(0, 0, { label: 'Delete', className: 'removeItem delete', action: this.deleteItem.bind(this, item) });
			}
			else if (item.changeOrderKey === changeOrderKey && item.isChange && !item.isAddition) {
				if (item.isModification) {
					items.splice(0, 0, { label: 'Revert to Original', className: 'changeRevert', action: this.revertItem.bind(this, item) }, { label: 'Duplicate', className: 'duplicateItem', action: this.duplicateItem.bind(this, item) });
				}
				else if (item.isDeletion) {
					items.splice(0, 0, { label: 'Restore', className: 'changeRevert', action: this.revertItem.bind(this, item) });
				}
			}
			else {
				items.splice(0, 0, { label: 'Duplicate', className: 'duplicateItem', action: this.duplicateItem.bind(this, item) }, { label: 'Delete', className: 'removeItem delete', action: this.deleteItem.bind(this, item) });
			}

			if (!isValid) {
				_.forEach(_.filter(items, { label: 'Duplicate' }), x => { x.className += ' disabled'; });
			}

			if (items.length) {
				const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

				systemActions.pushModal({ component: popup });
			}
		}

		sortRows (model) {
			const sortedItems = _.sortBy(_.filter(config.listAccessor(model), config.listFilterThunkFactory(this.props)), 'sortIndex');
			const lastIndex = sortedItems.length - 1;

			// prior to change tracking, keep sort indexes as clean integers, subsequently avoid mutations to items other than the source item
			_.forEach(sortedItems, (item, idx) => {
				// prior to change tracking, keep sort indexes as clean integers, subsequently avoid mutations to items other than the source item
				if (model.currentChangeOrder) {
					if (idx === 0) {
						if (lastIndex > 0) {
							const nextItem = sortedItems[idx + 1];
							if (nextItem.sortIndex <= item.sortIndex) {
								item.sortIndex = Math.ceil(nextItem.sortIndex - 1);
							}
						}
					}
					else {
						const previousItem = sortedItems[idx - 1];
						if (previousItem.sortIndex >= idx) {
							if (idx === lastIndex) {
								item.sortIndex = Math.floor(previousItem.sortIndex + 1);
							}
							else {
								const nextItem = sortedItems[idx + 1];
								if (nextItem.sortIndex > previousItem.sortIndex) {
									item.sortIndex = previousItem.sortIndex + ((nextItem.sortIndex - previousItem.sortIndex) / 2);
								}
								else {
									item.sortIndex = Math.floor(previousItem.sortIndex + 1);
								}
							}
						}
					}
				}
				else {
					item.sortIndex = idx;
				}
			});
		}

		getNextSortIndex (model) {
			return _.reduce(_.filter(config.listAccessor(model), config.listFilterThunkFactory(this.props)), (result, item) => {
				if (item.sortIndex >= result) {
					result = Math.floor(item.sortIndex + 1);
				}
				return result;
			}, 1);
		}

		handleItemDrop (model, sourceItem, targetItem) {
			const targetSortIndex = targetItem.sortIndex;
			const filteredItems = _.filter(config.listAccessor(model), config.listFilterThunkFactory(this.props));

			// prior to change tracking, keep sort indexes as clean integers, subsequently avoid mutations to items other than the source item
			if (model.currentChangeOrder) {
				if (sourceItem.sortIndex > targetSortIndex) {
					// find the item just before target item if it exists
					const priorItem = _.last(_.sortBy(_.filter(filteredItems, item => item.sortIndex < targetSortIndex), ['sortIndex']));
					const priorIndex = priorItem && priorItem.sortIndex || (targetSortIndex - 1);

					// set the new sort index to half way between the prior index and the target index
					sourceItem.sortIndex = targetSortIndex - ((targetSortIndex - priorIndex) / 2);
				}
				else {
					// find the item just after target item if it exists
					const nextItem = _.first(_.sortBy(_.filter(filteredItems, item => item.sortIndex > targetSortIndex), ['sortIndex']));
					const nextIndex = nextItem && nextItem.sortIndex || (targetSortIndex + 1);

					// set the new sort index to half way between the next index and the target index
					sourceItem.sortIndex = targetSortIndex + ((nextIndex - targetSortIndex) / 2);
				}
			}
			else {
				if (sourceItem.sortIndex > targetSortIndex) {
					_.forEach(filteredItems, item => {
						if (item.sortIndex >= targetSortIndex) {
							item.sortIndex += 1;
						}
					});
				}
				else {
					_.forEach(filteredItems, item => {
						if (item.sortIndex <= targetSortIndex) {
							item.sortIndex -= 1;
						}
					});
				}

				sourceItem.sortIndex = targetSortIndex;
			}
		}

		deleteItem (item) {
			systemActions.popModal();

			this.props.updateModel((model, childUpdateCallback) => {
				config.listAccessor(model).delete(item.key);
				this.sortRows(model);
				childUpdateCallback(item, '', true);
			}, { autoSave: true });
		}

		revertItem (item) {
			systemActions.popModal();

			this.props.updateModel((model, childUpdateCallback) => {
				config.listAccessor(model).revert(item.key);
				// notify as a deletion
				childUpdateCallback(item, '', true);
			}, { autoSave: true });
		}

		showHistory (item) {
			const { jobCost } = this.props;
			const modal = <ChangeHistoryModal close={ systemActions.popModal } item={ item } jobCost={ jobCost.contractProxy || jobCost } />;

			systemActions.popModal();
			systemActions.pushModal({ component: modal });
		}

		duplicateItem (item) {
			systemActions.popModal();

			this.props.updateModel((model, childUpdateCallback) => {
				if (item) {
					const list = config.listAccessor(model);
					const duplicate = item.duplicate();
					duplicate.sortIndex += 0.5;
					list.add(duplicate);
					this.sortRows(model);
					childUpdateCallback(item);
				}
			}, { autoSave: true });
		}

		getRowClasses ({ baseClassNames, item, readOnlyForAllowedEdits = 'none' }) {
			const { jobCost } = this.props;
			const rowClassNames = baseClassNames || ['added', 'dropTarget'];
			const changeOrderKey = jobCost.currentChangeOrder && jobCost.currentChangeOrder.key;
			const currentModule = componentServices.routeParams.modulekey;
			let isLocked = jobCost.allowedContractEdits === readOnlyForAllowedEdits;

			if (item.isChange) {
				if (!changeOrderKey || item.changeOrderKey === changeOrderKey) {
					rowClassNames.push('changed');
					if (item.action === enums.trackableActions.Set) {
						rowClassNames.push(item.rootKey === item.key ? 'new' : 'modified');
					}
					else {
						rowClassNames.push('removed');
					}
				} else if (changeOrderKey && item.changeOrderKey !== changeOrderKey && item.isDeletion) {
					rowClassNames.push('changed', 'removed', 'readOnly');
				}
			}

			// if there are change orders created for a job cost, original values should not be mutated under any circumstances
			if (isLocked && !changeOrderKey && (!jobCost.changeOrders || !jobCost.changeOrders.length())) {
				isLocked = !componentServices.securityContext.currentUserHasFeature(currentModule, 'edit-locked-jobcost');
			}

			if (isLocked) {
				rowClassNames.push('readOnly');
			}

			return rowClassNames;
		}

		render () {
			const props = {};

			_.assign(props, this.props, {
				getRowClasses: this.getRowClasses,
				getNextSortIndex: this.getNextSortIndex,
				handleItemDrop: this.handleItemDrop,
				rowContextMenuHandler: this.rowContextMenuHandler,
				sortRows: this.sortRows
			});

			return <InnerComponent {...props} />;
		}
	};
};

const JobCostHiddenWhenLocked = ({ children, jobCost }) => {
	const securityContext = componentServices.securityContext;
	const currentModule = componentServices.routeParams.modulekey;
	let isLocked = jobCost.allowedContractEdits === 'none';

	// if there are change orders created for a job cost, original values should not be mutated under any circumstances
	if (isLocked && (!jobCost.changeOrders || !jobCost.changeOrders.length())) {
		isLocked = !securityContext.currentUserHasFeature(currentModule, 'edit-locked-jobcost');
	}

	if (!isLocked && children && React.Children.count(children)) {
		return React.Children.only(children);
	}

	return null;
};

export const Lockable = ({ children, isLocked, className = undefined, classNames = undefined, lockedClass = 'disabled', ...rest }) => {
	const lockedProps = {readOnly: true, onChange: null, onBlur: null} as any;
	
	if(isLocked) {
		if(classNames && isLocked) {
			classNames.push(lockedClass);
			lockedProps.classNames = classNames
		}

		lockedProps.className = className? `${className} ${lockedClass}`: lockedClass 
	}

	return isLocked && children ? React.cloneElement(children, _.assign({}, rest, lockedProps)): children;
}


/**
 * @param isLockedOverride - A boolean that, when defined, overrides the default calculation for the 'isLocked' value for a JobCostLockable and instead uses the boolean value passed to it.
 */
const JobCostLockable = ({ children, jobCost, lockedClass = 'disabled', preventClaimOverride = false, lockChangeOrders = false, isLockedOverride = undefined }) => {
	const securityContext = componentServices.securityContext;
	const currentModule = componentServices.routeParams.modulekey;
	const hasLockedOverride = typeof isLockedOverride !== 'undefined';
	let isLocked = hasLockedOverride ? isLockedOverride : jobCost.allowedContractEdits === 'none' || (lockChangeOrders && !!jobCost.currentChangeOrder) ;

	// if there are change orders created for a job cost, original values should not be mutated under any circumstances
	if (!hasLockedOverride && isLocked && !preventClaimOverride && (!jobCost.changeOrders || !jobCost.changeOrders.length())) {
		isLocked = !securityContext.currentUserHasFeature(currentModule, 'edit-locked-jobcost');
	}

	return children({ isLocked: isLocked, getClassName: (classNames:string[] = []) => isLocked ? classNames.concat(lockedClass).join(' ') : classNames.join(' ') });
};

const JobSearchResults = sortableComponent(({ onJobClick, parameterName, searchText, updateSort, sortKey, list, nonExpanding }) => {
	const isContactSearch = parameterName === 'Contact Name';
	const rows = _.map(list, j => {
		const names = isContactSearch ? j.contactNames : j.clientNames;

		return (<tr key={j.key} onClick={() => onJobClick(j) }>
			<td />
			<td>{ j.accountingId || '-' }</td>
			<td>{ j.relayNumber }</td>
			<td>{ j.builderJobNumber || '-' }</td>
			<td>{ (j.workSiteAddress && j.workSiteAddress.street) || '-' }</td>
			<td>{ names || '-' }</td>
			<td />
			<td />
		</tr>);
	});

	const getClassName = (key, classes: string[] = []) => {
		if (key === sortKey) {
			classes.push('sorted');
		}

		return classes.join(' ');
	};

	const resultsLabel = !list ? 'NO RESULTS' : list.length === 1 ? '1 RESULT' : `${list.length} RESULTS`;
	const headerButton = !nonExpanding ? (<button><span>{ resultsLabel } - { parameterName } "{ searchText }"</span></button>) : null;
	const expanderIcon = !nonExpanding ? (<div className="iconExpander"><span /><span /></div>) : null;
	const contentClass = !nonExpanding ? '' : 'inner';

	return (<section className="expander expanded">
		<header>
			{ headerButton } { expanderIcon }
		</header>
		<div className={ contentClass }>
			<table className="sortable interactive" cellPadding="0" cellSpacing="0">
				<thead>
					<tr>
						<td />
						<td className={ getClassName('accountingId') } onClick={ () => updateSort('accountingId') }>{ globalRelay.config!.getString('Accounting ID', 'job') }</td>
						<td className={ getClassName('relayNumber') } onClick={ () => updateSort('relayNumber') }>Relay No.</td>
						<td className={ getClassName('builderJobNumber') } onClick={ () => updateSort('builderJobNumber') }>Builder Job No.</td>
						<td className={ getClassName('workSiteAddress/street') } onClick={ () => updateSort('workSiteAddress/street') }>Address</td>
						<td className={ getClassName(isContactSearch ? 'contactNames' : 'clientNames') } onClick={ () => updateSort(isContactSearch ? 'contactNames' : 'clientNames') }>{ isContactSearch ? 'Contact Name' : 'Client Name' }</td>
						<td />
						<td />
					</tr>
				</thead>
				<tbody>
					{ rows }
				</tbody>
			</table>
		</div>
	</section>);
});

class NumericInput extends React.Component<any, any> {
	private _innerValue;

	constructor (props) {
		super(props);

		this._innerValue = '';
		this.state = { hasFocus: false };

		this.handleOnBlur = this.handleOnBlur.bind(this);
		this.handleOnFocus = this.handleOnFocus.bind(this);
		this.handleOnChange = this.handleOnChange.bind(this);
	}

	handleOnBlur (e) {
		this.setState({ hasFocus: false });

		// On blur, make sure we reset the value to a maximum of 2 digits.
		const currentValue = valueFilters.toNumber(e.target.value);

		e.target.value = isNaN(currentValue) ? e.target.value : currentValue;

		// Call the parent onBlur if it is set
		if (this.props.onBlur) {
			this.props.onBlur(e);
		}
	}

	handleOnFocus (e) {
		const { onFocus, preventSelectOnFocus } = this.props;
		const target = e.target;
		const numberVal = valueFilters.toNumber(valueFilters.cleanNumber(target.value));

		this._innerValue = isNaN(numberVal) ? '0' : numberVal.toString();

		this.setState({ hasFocus: true });

		// Call the parent onFocus if it is set
		if (onFocus) {
			onFocus(e);
		}
		// NOTE: (DR) removed this because it caused stranging "jumping" on installation screen when quickly entering values
		if (!preventSelectOnFocus) {
			setTimeout(() => { target.select(); }, 0);
		}
	}

	handleOnChange (e) {
		const target = e.target;
		const { valueConverter } = this.props;

		this._innerValue = valueConverter ? valueConverter(target.value) : valueFilters.cleanNumber(target.value) || '';
		e.target.value = this._innerValue;

		if (this.props.onChange) {
			this.props.onChange(e);
		}
	}

	formatValue (val) {
		// If the user is currently typing in the field, do not format until the onBlur event
		if (this.state.hasFocus) {
			return this._innerValue;
		}
		else {
			const result = Number(val);

			return isNaN(result) ? undefined : result;
		}
	}

	render () {
		const { dataListOptions, dataListKey, value, ...rest } = this.props;
		const childProps = _.omit(rest, 'onBlur', 'onChange', 'value', 'preventSelectOnFocus');

		return (
			<React.Fragment>
				<input type="text" className={ dataListOptions ? 'dataList' : undefined } list={ dataListOptions ? dataListKey : undefined } onChange={ this.handleOnChange } onFocus={ this.handleOnFocus } onBlur={ this.handleOnBlur } value={this.formatValue(value)} {...childProps} />
				{ dataListOptions ? <datalist id={ dataListKey }> { dataListOptions }</datalist> : null }
			</React.Fragment>
		);
	}
}

class PercentInput extends React.Component<any, any> {
	private _innerValue;

	constructor (props) {
		super(props);

		this._innerValue = '';
		this.state = { hasFocus: false };

		this.handleEvent = this.handleEvent.bind(this);
	}

	handleEvent (e) {
		const target = e.target;

		this._innerValue = valueFilters.cleanNumber(target.value) || '';
		target.value = this._innerValue === '' ? undefined : (Number(this._innerValue) / 100).toFixed(4);

		if (e.type === 'focus') {
			this.setState({ hasFocus: true });
			setTimeout(() => { target.select(); }, 0);
		}
		else if (e.type === 'blur') {
			this.setState({ hasFocus: false });
			this._innerValue = '';
		}

		const handler = this.props['on' + _.upperFirst(e.type)];

		if (handler) {
			handler(e);
		}
	}

	formatValue (val) {
		if (this.state.hasFocus) {
			return this._innerValue;
		}
		else {
			const valAsNumber = Number(val);
			return isNaN(valAsNumber) ? '' : (valAsNumber * 100).toFixed(2);
		}
	}

	render () {
		const { dataListOptions, dataListKey, value, ...rest } = this.props;
		const childProps = _.omit(rest, 'onBlur', 'onChange', 'onFocus');

		return (<React.Fragment>
			<input type="text" className={ dataListOptions ? 'dataList' : undefined } list={ dataListOptions ? dataListKey : undefined } onChange={ this.handleEvent } onFocus={ this.handleEvent } onBlur={ this.handleEvent } value={this.formatValue(value)} {...childProps} />
			{ dataListOptions ? <datalist id={ dataListKey }> { dataListOptions }</datalist> : null }
		</React.Fragment>
		);
	}
}

class PhoneInput extends React.Component<any, any> {
	private _innerValue;

	constructor (props) {
		super(props);

		this._innerValue = '';
		this.state = { hasFocus: false };

		this.handleEvent = this.handleEvent.bind(this);
	}

	handleEvent (e) {
		const target = e.target;
		let handler;

		if (e.type === 'focus') {
			handler = this.props.onFocus;
			this._innerValue = target.value;
			this.setState({ hasFocus: true });
			setTimeout(() => { target.select(); }, 0);
		}
		else if (e.type === 'blur') {
			handler = this.props.onBlur;
			this.setState({ hasFocus: false });
			this._innerValue = '';
			target.value = validator.isPhone(target.value) ? util.phoneHelper.formatInternational(target.value) : target.value;
		}
		else if (e.type === 'change') {
			handler = this.props.onChange;
			const val = util.phoneHelper.tryFormat(target.value);
			this._innerValue = validator.isPhone(val) ? util.phoneHelper.formatInternational(val) : val;
			target.value = this._innerValue;
		}

		if (handler) {
			handler(e);
		}
	}

	formatValue (val) {
		if (this.state.hasFocus) {
			return this._innerValue;
		}
		else {
			return val;
		}
	}

	render () {
		const { value, ...rest } = this.props;
		const childProps = _.omit(rest, 'onBlur', 'onChange', 'value');

		return (
			<input type="text" onChange={ this.handleEvent } onFocus={ this.handleEvent } onBlur={ this.handleEvent } value={this.formatValue(value)} {...childProps}/>
		);
	}
}

class PhotoModal extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.handleDeleteClick = this.handleDeleteClick.bind(this);
	}

	handleDeleteClick () {
		const { file, preventDelete } = this.props;
		const doDelete = () => {
			systemActions.popModal();
			fileActions.deleteFile({ file });
			systemActions.popModal();
		};
		const popup = <Confirm title={ 'Permanently Delete Photo?' } message={ 'Are you sure you want to permanently delete this Photo? This cannot be undone.' } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ systemActions.popModal } confirmHandler={ doDelete } />;

		if (preventDelete) {
			return;
		}

		systemActions.pushModal({ component: popup });
	}

	render () {
		const { file, preventDelete } = this.props;
		const deleteClasses = preventDelete ? 'delete disabled' : 'delete';

		return (<div className="window">
			<header>
				<button className={ deleteClasses } onClick={ preventDelete ? undefined : this.handleDeleteClick }><span>Delete</span></button>
				<span>Photo</span><button className="close" onClick={ systemActions.popModal }><span>Close</span></button>
			</header>
			<div className="views">
				<div className="content in">
					<img src={ file.url } />
				</div>
			</div>
		</div>);
	}
}

class PinnableStats extends React.Component<any, any> {
	constructor (props) {
		super(props);

		// this.el = document.createElement('div');
		// this.el.className = 'modal pinned in';
	}

	// componentDidMount () {
	// 	const appRoot = document.getElementById('appRoot');
	// 	appRoot.appendChild(this.el);
	// }

	// componentWillUnmount () {
	// 	const appRoot = document.getElementById('appRoot');
	// 	appRoot.removeChild(this.el);
	// }
	render () {
		const { children, isPinned } = this.props;

		if (!isPinned) {
			return children;
		}
		else {
			const el = document.getElementById('pinnedStats');

			return el ? ReactDOM.createPortal(<React.Fragment>{ children }<button className="icon iconOnly unPinItem" onClick={ systemActions.clearPinnedStats }><span /></button></React.Fragment>, el) : null;
		}
	}
}

export class Popup extends React.Component<any, any> {
	private _childProps;
	private _rootRef:React.RefObject<HTMLDivElement>;
	private _containerRef:React.RefObject<HTMLDivElement>;
	private _wedgeRef:React.RefObject<HTMLDivElement>;

	constructor (props) {
		super(props);

		this._rootRef = React.createRef();
		this._containerRef = React.createRef();
		this._wedgeRef  = React.createRef();

		this._childProps = {};
	}

	componentDidMount () {
		const windowSize = componentServices.store.getIn(['viewState', 'metrics', 'windowSize']).toJS();
		const isVertical = this.props.isVertical;
		const target = typeof this.props.target === 'function' ? this.props.target() : this.props.target;
		const popupRoot = this._rootRef.current!;
		const container = this._containerRef.current!;
		const wedge = this._wedgeRef.current!;
		const content = container.querySelector(this.props.contentSelector) || container.firstChild;
		const popupRect = popupRoot.getBoundingClientRect();
		const contentRect = content.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		const targetCenter = { x: targetRect.left + (targetRect.width / 2), y: targetRect.top + (targetRect.height / 2) };
		const pixelsFromBottom = windowSize.height - targetRect.bottom;
		const pixelsFromRight = windowSize.width - targetRect.right;
		const constrainedBottom = pixelsFromBottom < targetRect.top;
		const constrainedRight = pixelsFromRight < targetRect.left;
		const rootStyles:any = { }; // pointerEvents: "none"
		const bottomOverflow = Math.min(contentRect.height,targetRect.top)/2+targetCenter.y>windowSize.height;
		const topOverflow = targetCenter.y-Math.min(contentRect.height,pixelsFromBottom)/2<0;
		let maxHeight = 0;

		if (isVertical) {
			rootStyles.marginLeft = -(contentRect.width / 2) + 'px';
			if (constrainedRight) {
				rootStyles.left = 'auto';
				rootStyles.right = Math.max((windowSize.width - targetCenter.x) - (contentRect.width / 2), 20) + 'px';
			}
			else {
				rootStyles.right = 'auto';
				rootStyles.left = Math.max(targetCenter.x, contentRect.width / 2) + 'px';
			}

			if (constrainedBottom) {
				maxHeight = targetRect.top;
				popupRoot.className = [popupRoot.className, 'up'].join(' ');
				container.style.maxHeight = targetRect.top + 'px';
				rootStyles.bottom = (pixelsFromBottom + Math.min(maxHeight, targetRect.height)) + 'px';
				rootStyles.top = 'auto';
			}
			else {
				maxHeight = pixelsFromBottom;
				popupRoot.className = [popupRoot.className, 'down'].join(' ');
				container.style.maxHeight = pixelsFromBottom + 'px';
				rootStyles.top = Math.min(maxHeight, targetRect.bottom) + 'px';
				rootStyles.bottom = 'auto';
			}
		}
		else {
			if (constrainedRight) {
				popupRoot.className = [popupRoot.className, 'left'].join(' ');
				container.style.maxWidth = targetRect.left + 'px';
				rootStyles.right = (windowSize.width - targetRect.left) + 'px';
				rootStyles.left = 'auto';
			}
			else {
				popupRoot.className = [popupRoot.className, 'right'].join(' ');
				container.style.maxWidth = targetRect.right + 'px';
				rootStyles.left = targetRect.right + 'px';
				rootStyles.right = 'auto';
			}

			// TODO: shift the popup and callout wedge if scrolling will hide a portion
			if (constrainedBottom) {
				maxHeight = targetRect.top;
				container.style.maxHeight = targetRect.top + 'px';
			}
			else {
				maxHeight = pixelsFromBottom;
				container.style.maxHeight = pixelsFromBottom + 'px';
			}
			if (bottomOverflow) {
				rootStyles.bottom = Math.min(Math.max(pixelsFromBottom, 0), 20) + 'px';
				rootStyles.top = 'auto';
				wedge.style.top = 'calc(' + targetCenter.y + 'px + ' + -windowSize.height + 'px + ' + Math.min(contentRect.height, maxHeight) + 'px + ' + rootStyles.bottom + ')';
			}
			else if (topOverflow) {
				rootStyles.top = Math.max(20, Math.min(targetRect.top, (targetCenter.y - Math.min(maxHeight, contentRect.height) / 2))) + 'px';
				rootStyles.bottom = 'auto';
				wedge.style.top = 'calc(' + targetCenter.y + 'px - ' + rootStyles.top + ')';
			}
			else {

				rootStyles.bottom = 'auto';
				rootStyles.top = (targetCenter.y - Math.min(maxHeight, contentRect.height) / 2) + 'px';
			}
			wedge.style.bottom = 'auto';
		}

		_.forIn(rootStyles, function (v, k) {
			popupRoot.style[k] = v;
		});

		this._childProps = { style: { maxHeight: maxHeight + 'px' }};
	}

	render () {
		const { classNames = [], children } = this.props;
		const classes = _.concat(['popup'], classNames);

		return (
			<div className={classes.join(' ')} ref={ this._rootRef }>
				<div className="wedge" ref={ this._wedgeRef }/>
				<div className="container" ref={ this._containerRef }>
					{ React.cloneElement(children, this._childProps) }
				</div>
			</div>
		);
	}
}

interface RadioButton { 
	key?: any; 
	value?: any; 
	label?: string | number; 
	isSelected?: boolean; 
	classNames?:string[] 
}

function Radio({ buttons, classNames, onChange, reportAllEvents, ...childProps }: {
    [x: string]: any;
    buttons: RadioButton[];
    classNames?: string[];
    onChange(selected: RadioButton, current: RadioButton):void;
    reportAllEvents?: boolean;
}) {
	const classes = _.filter(_.concat(['radio'], classNames), className => !!className);
	const handleClick = useCallback((button) => {
		const currentButton = buttons.find(b => b.isSelected);

		if (currentButton !== button || reportAllEvents) {
			onChange(button, currentButton);
		}
	}, [buttons, reportAllEvents]);
	const formatClassName = useCallback((button) => {
		const buttonClassNames = button.classNames || ['icon'];

		if (button.isSelected) {
			buttonClassNames.push('on');
		}
		return buttonClassNames.join(' ');
	}, []);
	return (
		<div className={ classes.join(' ') }>
			{ buttons.map(button => <Button label={button.label} className={formatClassName(button)} onClick={() => handleClick(button)} key={button.key || button.value} {...childProps} />) }
		</div>
	);
}
// class Radio extends React.PureComponent<any> {
// 	handleClick (button) {
// 		const { reportAllEvents } = this.props;
// 		const currentButton = _.find(this.props.buttons, { isSelected: true });

// 		if (currentButton !== button || reportAllEvents) {
// 			this.props.onChange(button, currentButton);
// 		}
// 	}

// 	render () {
// 		const { buttons, classNames, ...childProps } = this.props;
// 		const classes = _.filter(_.concat(['radio'], classNames), className => !!className);

// 		function formatClassName (button) {
// 			const buttonClassNames = button.classNames || ['icon'];

// 			if (button.isSelected) {
// 				buttonClassNames.push('on');
// 			}
// 			return buttonClassNames.join(' ');
// 		}

// 		const buttonList:JSX.Element[] = [];

// 		_.forEach(buttons, button => {
// 			buttonList.push(<Button label={button.label} className={formatClassName(button)} onClick={this.handleClick.bind(this, button)} key={button.key || button.value} {...childProps} />);
// 		});

// 		return (
// 			<div className={ classes.join(' ') }>
// 				{buttonList}
// 			</div>
// 		);
// 	}
// }

class SalesPerformanceChart extends React.Component<any, any> {
	private _statsRef: React.RefObject<HTMLDivElement>;

	constructor (props) {
		super(props);

		this.handleRingChartClick = this.handleRingChartClick.bind(this);
		this._statsRef = React.createRef();
	}

	getPopupContent (stats) {
		const { countLeads, countProspects, countCustomers, sumProjected, sumContracted, title } = stats;

		return (<div className="content">
			<h2>{ title }</h2>
			<table className="compact" cellPadding="0" cellSpacing="0">
				<tbody>
					<tr>
						<td />
						<td><strong>Leads</strong></td>
						<td className="quantity">{ countLeads }</td>
						<td />
					</tr>
					<tr>
						<td />
						<td><strong>Prospects</strong></td>
						<td className="quantity">{ countProspects }</td>
						<td />
					</tr>
					<tr>
						<td />
						<td><strong>Clients</strong></td>
						<td className="quantity">{ countCustomers }</td>
						<td />
					</tr>
				</tbody>
			</table>
			<table className="compact" cellPadding="0" cellSpacing="0">
				<tbody>
					<tr>
						<td />
						<td><strong>Projected</strong></td>
						<td className="cost">{ `$ ${util.formatScaled(sumProjected)}`}</td>
						<td />
					</tr>
					<tr>
						<td />
						<td className="green"><strong>Contracted</strong></td>
						<td className="cost green">{ `$ ${util.formatScaled(sumContracted)}`}</td>
						<td />
					</tr>
				</tbody>
			</table>
		</div>);
	}

	handleMonthClick (month, year, e) {
		const { aggregates = { sumGoal: 0, sumContracted: 0, sumProjected: 0 }} = this.props;
		const windowSize = componentServices.store.getIn(['viewState', 'metrics', 'windowSize']).toJS();
		const leadsKey = `leads_${year.toString()}_${month.toString()}`;
		const prospectsKey = `prospects_${year.toString()}_${month.toString()}`;
		const contractsKey = `contracts_${year.toString()}_${month.toString()}`;
		const projectedKey = `projected_${year.toString()}_${month.toString()}`;
		const contractedKey = `contracted_${year.toString()}_${month.toString()}`;

		const modalContent = (<Popup isVertical={true} target={e.target} classNames={['tip']} windowSize={windowSize}>
			{ this.getPopupContent({
				countLeads: Number(aggregates[leadsKey] || '0'),
				countProspects: Number(aggregates[prospectsKey] || '0'),
				countCustomers: Number(aggregates[contractsKey] || '0'),
				sumProjected: Number(aggregates[projectedKey] || '0'),
				sumContracted: Number(aggregates[contractedKey] || '0'),
				title: `${util.dateHelper.formatDate(new Date(year, month, 1), { format: 'MMMM YYYY' }).toUpperCase()}`
			})
			}
		</Popup>);

		systemActions.pushModal({ component: modalContent });
	}

	handleRingChartClick () {
		const { aggregates = { sumGoal: 0, sumContracted: 0, sumProjected: 0 }, fiscalYear } = this.props;
		const windowSize = componentServices.store.getIn(['viewState', 'metrics', 'windowSize']).toJS();

		const modalContent = (<Popup isVertical={true} target={ this._statsRef } classNames={['tip']} windowSize={windowSize}>
			{ this.getPopupContent({
				countLeads: aggregates.countLeads,
				countProspects: aggregates.countProspects,
				countCustomers: aggregates.countCustomers,
				sumProjected: aggregates.sumProjected,
				sumContracted: aggregates.sumContracted,
				title: `${util.dateHelper.formatDate(fiscalYear.startDate, { format: 'MMM YYYY' }).toUpperCase()} - ${util.dateHelper.formatDate(util.dateHelper.addMonths(fiscalYear.endDate, -1), { format: 'MMM YYYY' }).toUpperCase()}`
			})
			}
		</Popup>);

		systemActions.pushModal({ component: modalContent });
	}

	render () {
		const { aggregates = { sumGoal: 0, sumContracted: 0, sumProjected: 0 }, fiscalYear, onContextMenuClick, startDate, sumGoal = 0 } = this.props;
		const ringChartGoal = sumGoal || (aggregates.sumContracted + aggregates.sumProjected);
		let startYear = startDate.getFullYear();
		const months:any[] = [];
		const yAxis:any[] = [];
		let monthMax = 0;
		let scaledMax = 0;

		for (let i = 0; i < 12; i++) {
			const monthDate = util.dateHelper.addMonths(startDate, i);
			const year = monthDate.getFullYear();
			const month = monthDate.getMonth();
			const contracted = aggregates[`contracted_${year.toString()}_${month.toString()}`] || 0;
			const projected = aggregates[`projected_${year.toString()}_${month.toString()}`] || 0;
			const className = (monthDate >= fiscalYear.startDate && monthDate < fiscalYear.endDate) ? '' : 'nextYear';
			let label = util.dateHelper.formatDate(monthDate, { format: 'MMM' }).toUpperCase();

			if (contracted + projected > monthMax) {
				monthMax = contracted + projected;
			}

			if (startYear !== monthDate.getFullYear()) {
				startYear = monthDate.getFullYear();
				label += ` ${startYear.toString().substr(2)}`;
			}

			months.push({
				className,
				contracted,
				projected,
				label,
				month,
				year
			});
		}

		const log10Max = Math.floor(Math.log10(monthMax));
		const scale = Math.pow(10, log10Max);

		scaledMax = Math.ceil(monthMax / scale) * scale;

		for (let i = 1; i < 5; i++) {
			yAxis.push(<span key={i}>{ `$ ${util.formatScaled(scaledMax * (i / 4))}` }</span>);
		}

		return (<div className="chart performanceChart">
			<div className="barChart">
				{ onContextMenuClick ? <button className="icon iconOnly contextMenu" onClick={ onContextMenuClick }><span /></button> : null }
				<div className="axis y">
					<span>$ 0 </span>
					{ yAxis }
				</div>
				<div className="data">
					<div className="bars">
						{ _.map(months, m => {
							return (<div className="bar" key={ m.label }>
								{ m.projected ? <span className={ m.className } style={{ height: `${(m.projected / scaledMax) * 100}%` }} /> : null }
								{ m.contracted ? <span className={ [m.className, 'contracted'].join(' ') } style={{ height: `${(m.contracted / scaledMax) * 100}%` }} /> : null }
							</div>);
						})
						}

						<div className="ticMarks">
							<span />
							<span />
							<span />
						</div>
					</div>
					<div className="axis x">
						{ _.map(months, m => <span className={ m.className } key={ m.label } onClick={ this.handleMonthClick.bind(this, m.month, m.year) }>{ m.label } </span>) }
					</div>
				</div>
			</div>
			<div className="ringChart" onClick={ this.handleRingChartClick }>
				<svg viewBox="0 0 100 100">
					<circle r="50" cx="50" cy="50" className="goal" />
					<circle r="50" cx="50" cy="50" className="projected" strokeDasharray={`${((aggregates.sumContracted + aggregates.sumProjected) / ringChartGoal) * 315} 315`} transform="rotate(-90 50 50)" />
					<circle r="50" cx="50" cy="50" className="contracted" strokeDasharray={ `${(aggregates.sumContracted / ringChartGoal) * 315} 315`} transform="rotate(-90 50 50)" />
					<circle r="50" cx="50" cy="50" className="cover" />
				</svg>
				<div className="stats">
					<div ref={ this._statsRef }>
						<span>{ `$ ${util.formatScaled(ringChartGoal)} GOAL` }</span>{ `$ ${util.formatScaled(aggregates.sumProjected + aggregates.sumContracted)}` }<span>TOTAL TAX YEAR</span>
					</div>
				</div>
			</div>
		</div>);
	}
}

class SearchForm extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.state = {
			searchText: props.initialSearchText || '',
			searchParameter: props.initialActiveParameter ? props.initialActiveParameter : (props.searchParameters && props.searchParameters.length) ? props.searchParameters[0] : {},
			searchTextTest: props.searchTextTest || /[^\s]{3}/
		};

		this.handleSearchClick = this.handleSearchClick.bind(this);
		this.handleKeyPress = this.handleKeyPress.bind(this);
		this.handleSearchTextChange = this.handleSearchTextChange.bind(this);
		this.handleParameterClick = this.handleParameterClick.bind(this);
	}

	doSearch () {
		const { searchParameter, searchText, searchTextTest } = this.state;
		const test = (searchParameter && searchParameter.searchTextTest) || searchTextTest;

		if (test.test(searchText)) {
			this.props.onSearch(this.state);
		}
	}

	handleKeyPress (e) {
		if (e.which === 13) {
			e.preventDefault();
			this.doSearch();
		}
	}

	handleSearchClick (e) {
		e.preventDefault();

		this.doSearch();
	}

	handleSearchTextChange (e) {
		const searchText = e.target.value;

		this.setState({ searchText: e.target.value });

		if (this.props.onSearchCleared && !valueFilters.clean(searchText)) {
			this.props.onSearchCleared();
		}
	}

	handleParameterClick (e) {
		const { searchParameters } = this.props;
		const { searchParameter } = this.state;
		const windowSize = componentServices.store.getIn(['viewState', 'metrics', 'windowSize']).toJS();
		const items = _.map(searchParameters, p => {
			const buttonClassNames = (searchParameter && searchParameter.key === p.key) ? ['button', 'on'] : ['button'];
			return (<li key={ p.key }><a className={ buttonClassNames.join(' ')} onClick={ () => { systemActions.popModal(); this.setState({ searchParameter: p }); } }><span>{ p.name }</span></a></li>);
		});

		const modalContent = (<Popup isVertical={true} target={e.target} windowSize={windowSize}>
			<div className="content scrollable">
				<ul className="menu">
					{ items }
				</ul>
			</div>
		</Popup>);

		systemActions.pushModal({ component: modalContent });
	}

	render () {
		const { searchParameters } = this.props;
		const { searchParameter, searchText, searchTextTest } = this.state;
		const parameterButton = searchParameters && searchParameters.length
			? <button className="icon iconLabel iconRight searchParameter" onClick={ this.handleParameterClick }><span>{ searchParameter.name }</span></button>
			: null;
		const test = (searchParameter && searchParameter.searchTextTest) || searchTextTest;
		const searchButtonClasses = test.test(searchText) ? ['icon', 'search'] : ['icon', 'search', 'disabled'];

		return (
			<div className="searchForm" onKeyPress={this.handleKeyPress}>
				{ parameterButton }
				<input type="search" placeholder="Search" autoCapitalize="none" autoCorrect="off" value={ searchText } onChange={ this.handleSearchTextChange } />
				<button className={ searchButtonClasses.join(' ')} onClick={this.handleSearchClick}><span>&nbsp;</span></button>
			</div>
		);
	}
}

const Section = ({ title, actions, options, readOnly, children }: { title: any, actions?: any, options?: any, readOnly?:boolean, children: any }) => {
	return 	(<section>
		<Header title={title} actions={actions} options={options} />
		<div className={ readOnly ? 'readOnly' : '' }>
			{children}
		</div>
	</section>);
};

const Spinner = ({ cancelAction = undefined, classNames = []}: { cancelAction?: any, classNames?: string[]}) => {
	const button = cancelAction ? <button className="cancel" onClick={ () => cancelAction() } /> : null;

	classNames.push('progress');

	return (<span className="loader"><span className={ classNames.join(' ') } />{ button }</span>);
};

let _tooltipKey = 0;
function getTooltipKey () {
	if (_tooltipKey === Number.MAX_SAFE_INTEGER) {
		_tooltipKey = 0;
	}
	else {
		_tooltipKey++;
	}

	return `global-tooltip-${_tooltipKey}`;
}
class TooltipTarget extends React.Component<any, any> {
	tooltipKey;

	constructor (props) {
		super(props);

		this.handleMouseOver = this.handleMouseOver.bind(this);
		this.handleMouseOut = this.handleMouseOut.bind(this);
		this.tooltipKey = getTooltipKey();
	}
	// componentDidMount () {
	// 	const element = ReactDOM.findDOMNode(this);

	// 	console.log(element);
	// }

	handleMouseOver () {
		const { text } = this.props;
		const target = ReactDOM.findDOMNode(this);

		if (text && target) {
			systemActions.setTooltip({
				key: this.tooltipKey,
				component: <Popup classNames={ ['tooltip'] } target={ target }><div className="content scrollable">{ text }</div></Popup>,
				props: { classNames: ['floating'], transitionExit: 0 }
			});
		}
	}

	handleMouseOut () {
		const { text } = this.props;

		if (text) {
			systemActions.setTooltip(null);
		}
	}

	render () {
		const { children } = this.props;

		return React.cloneElement(children, { onMouseOver: this.handleMouseOver, onMouseOut: this.handleMouseOut });
	}
}

class ViewPager extends React.Component<any, any> {
	constructor (props) {
		super(props);

		this.state = this.getStateFromProps(this.props);
	}

	UNSAFE_componentWillReceiveProps (nextProps) {
		this.setState(this.getStateFromProps(nextProps));
	}

	getStateFromProps (props) {
		return {
			lastIndex: (props) ? props.currentIndex : 0
		};
	}

	render () {
		const { currentIndex, classes = []} = this.props;
		let index = 0;
		const pages = React.Children.map(this.props.views, (view) => {
			let result:any = null;
			const className = classes[index] || '';

			if (index <= currentIndex) {
				result = (<ViewPage key={index} classes={(index < currentIndex) ? [className, 'out'] : [className]} page={index}>{view}</ViewPage>);
			}
			index++;
			return result;
		});

		return (
			<TransitionGroup component="div" className="views">
				{ pages }
			</TransitionGroup>
		);
	}
}

class ViewPage extends React.Component<any, any> {
	constructor (props) {
		super(props);
	}

	render () {
		const { children, ...props } = this.props;
		// console.log('page ' + this.props.page + ' classNames ' + classNames.join(' '));

		return (<Transition timeout={{ enter: 10, exit: 210 }} appear={ false } { ...props }>{ state => {
			const { classes = [], inClass = 'in', outClass = 'out', page } = props;
			const classNames = ['content', 'scrollable'].concat(classes);

			if (['entering'].indexOf(state) !== -1 && page === 0) {
				classNames.push(inClass);
			}
			else if (['entered'].indexOf(state) !== -1 && classNames.indexOf(outClass) === -1) {
				classNames.push(inClass);
			}

			return (<div className={ classNames.join(' ') }>
				{ children }
			</div>);
		} }</Transition>);
	}
}

export function AsyncContent({ emptyContent=<div className="inner">No Data</div>, isFetching=false, hasData = false, children } : {emptyContent?: React.ReactNode, hasData?: boolean, isFetching?:boolean, children: any }) {
	const fallback = typeof emptyContent === 'string'? <div className="inner">{ emptyContent }</div>: emptyContent;
	
	return hasData? children: isFetching? <Spinner classNames={ ['dark'] } />: fallback;
}

export {
	Alert,
	AsyncButton,
	Avatar,
	Button,
	ColumnSelector,
	Confirm,
	ContextMenu,
	ContractorLockSection,
	CurrencyInput,
	EditableDiv,
	FileList,
	FractionInput,
	Header,
	jobCostRowOwner,
	JobCostHiddenWhenLocked,
	JobCostLockable,
	JobSearchResults,
	NumericInput,
	PercentInput,
	PinnableStats,
	PhoneInput,
	PhotoModal,
	Radio,
	SalesPerformanceChart,
	SearchForm,
	Section,
	sortableComponent,
	Spinner,
	TooltipTarget,
	ViewPage,
	ViewPager
};